<script setup>
import ActionCategoryIcon from "@/components/actions/partials/ActionCategoryIcon.vue";

import {computed, ref} from "vue";
import ActionPriority from "@/components/actions/partials/ActionPriority.vue";
import ActionTime from "@/components/actions/partials/ActionTime.vue";
import ActionUser from "@/components/actions/partials/ActionUser.vue";
import moment from "moment";
import MarkActionIncomplete from "@/components/actions/buttons/MarkActionIncomplete.vue";
import UnhideActionButton from "@/components/actions/partials/UnhideActionButton.vue";
import ActionRowContent from "@/components/actions/partials/ActionRowContent.vue";
import PendingIcon from "@/components/actions/partials/PendingIcon.vue";
import WarningIcon from "@/components/actions/partials/WarningIcon.vue";
import KitFixableIconTooltip from "@/components/actions/partials/KitFixableIconTooltip.vue";

const props = defineProps({
  action: {
    type: Object,
    required: true,
  },
  showPageUrl: {
    type: Boolean,
    required: false,
    default: false,
  },
  isHidden: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const expanded = ref(false);

const isComplete = computed(() => !!props.action.completed_at || !!props.action.pending_completed_at);
const completedAt = computed(() => isComplete.value ? moment(props.action.pending_completed_at) : null);
const showGapping = computed(() => (props.showPageUrl || isComplete.value || props.isHidden));
const onlyPendingCompleted = computed(() => props.action.pending_completed_at && !props.action.completed_at);
const shouldShowKitIcon = computed(() => (
  isComplete.value
    ? !!props.action.auto_fixed
    : props.action.kit_fixable
));
const showAutoFixedTooltip = computed(() => isComplete.value && !!props.action.auto_fixed);

const toggleExpanded = () => {
  if(props.isHidden || isComplete.value) {
    return;
  }
  expanded.value = !expanded.value;
}
</script>

<template>
  <div
    :class="[
      'tw-border tw-rounded-ld tw-border-grey-mid tw-rounded-lg',
      expanded ? 'tw-bg-grey-highlight' : 'tw-bg-white',
    ]"
  >
    <div
      class="tw-flex tw-gap-6 tw-p-6 tw-items-center"
      :class="{
        'tw-cursor-pointer': !isHidden && !isComplete,
      }"
      @click="toggleExpanded"
    >
      <div v-if="!isComplete">
        <ActionCategoryIcon :category="action.category" />
      </div>
      <div
        class="tw-grow-1 tw-w-full tw-flex tw-flex-col"
        :class="{
          'tw-gap-2' : showGapping && action.page?.url,
        }"
      >
        <div v-if="isComplete">
          <span class="tw-font-semibold tw-text-lg tw-text-font-secondary">{{
            completedAt.format('MMMM Do YYYY')
          }}</span>
        </div>
        <div class="tw-text-font tw-text-xl">
          {{ !isComplete ? action.title : action.completed_title }}
        </div>
        <div>
          <a
            v-if="action.page?.url && showGapping"
            class="tw-text-blue tw-text-sm"
            :href="action.page.url"
            target="_blank"
            @click.stop
          >
            {{ action.page.url }}
          </a>
        </div>
      </div>

      <KitFixableIconTooltip
        v-if="shouldShowKitIcon"
        :auto-fixed="showAutoFixedTooltip"
      />

      <div class="tw-shrink-0">
        <ActionPriority
          v-if="!isComplete && !isHidden"
          :priority="action.priority"
        />
        <ActionUser
          v-if="action.completed_by || action.pending_completed_by"
          :user-name="action.completed_by ?? action.pending_completed_by"
        />
      </div>
      <div
        class="tw-text-right"
        :class="{
          'tw-min-w-[6rem]': !onlyPendingCompleted && !action.check_failed
        }"
      >
        <ActionTime :minutes="action.action_time" />
      </div>
      <div
        v-if="isComplete"
        class="tw-flex tw-items-center tw-gap-6"
      >
        <PendingIcon v-if="onlyPendingCompleted" />
        <MarkActionIncomplete
          v-if="action.can_mark_incomplete"
          :action="action"
        />
      </div>
      <div v-if="isHidden">
        <UnhideActionButton :action="action" />
      </div>
      <div v-if="action.check_failed">
        <WarningIcon />
      </div>
    </div>
    <ActionRowContent
      v-show="expanded"
      :action="action"
    />
  </div>
</template>
